import React, { useCallback } from "react";
import { TabButton, TabButtonVariant } from "@ui-kit/TabButton/TabButton";
import { useTabMultiSelect } from "./useTabSelect";
import { TabValue, TabGroup } from "./Tab";

interface TabMultiSelectProps<T extends TabValue> {
  /**
   * Array of tab objects containing name and label
   */
  tabs: TabGroup<T>;
  /**
   * Initial active tab names
   */
  initialActiveTabs?: T[] | null;
  /**
   * Callback when tabs are selected
   */
  onTabSelect?: (tabValues: T[]) => void;
  /**
   * Callback with updated tabs after selection
   */
  afterTabSelect?: (tabValues: T[]) => void;
  /**
   * Size of the tab buttons
   */
  size?: "default" | "large" | "small" | "small-default";
  /**
   * If the tab buttons should be disabled
   */
  disabled?: boolean;
  /**
   * Variant of the tab buttons
   */
  variant?: TabButtonVariant;
  /**
   * Test ID for testing purposes
   */
  testID: string;
}

const TabMultiSelect = <T extends TabValue>({
  tabs,
  initialActiveTabs = null,
  afterTabSelect,
  size = "default",
  disabled = false,
  variant = "default",
  testID,
}: TabMultiSelectProps<T>) => {
  const { activeTabs, onTabSelect } = useTabMultiSelect<T>({
    initialTabs: initialActiveTabs,
    afterTabSelect,
  });

  const createTabHandler = useCallback(
    (tabValue: T) => {
      return () => onTabSelect(tabValue);
    },
    [onTabSelect],
  );

  return (
    <div
      data-testid={testID}
      style={{
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        justifyContent: "center",
      }}>
      {tabs.map((tab) => (
        <TabButton
          key={tab.value.toString()}
          label={tab.label}
          active={activeTabs.includes(tab.value)}
          onPress={createTabHandler(tab.value)}
          size={size}
          disabled={disabled}
          variant={variant}
          testID={`${testID}-${tab.value.toString()}`}
        />
      ))}
    </div>
  );
};

export { TabMultiSelect };
